import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonComponent } from '../button/button.component';
import { CollapsibleSectionComponent } from '../collapsible-section/collapsible-section.component';
import { DriversLicenses } from '../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { enums } from '../../../../../../libs/shared/src/lib/models/dashboard/job-offer/job-offer-enums';

// Available driver's license types
export const DRIVERS_LICENSE_TYPES = enums.driversLicenseTypes;
export type DriversLicenseType = (typeof DRIVERS_LICENSE_TYPES)[number];

@Component({
  selector: 'app-drivers-license-section',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoPipe, ButtonComponent, CollapsibleSectionComponent],
  templateUrl: './drivers-license-section.component.html',
  styleUrls: ['./drivers-license-section.component.scss'],
})
export class DriversLicenseSectionComponent implements OnInit, OnChanges {
  @Input() driversLicenses: DriversLicenses | null = null;
  @Input() isAuthenticated = false;

  @Output() driversLicenseChange = new EventEmitter<{
    action: 'save';
    licenses: string[];
  }>();

  // All available license types
  licenseTypes = DRIVERS_LICENSE_TYPES;

  // Selected licenses
  selectedLicenses: Set<string> = new Set();

  // Edit mode state
  isEditing = false;

  // Temporary selection during edit
  tempSelectedLicenses: Set<string> = new Set();

  ngOnInit(): void {
    this.parseExistingLicenses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['driversLicenses']) {
      this.parseExistingLicenses();
    }
  }

  /**
   * Parse the existing drivers licenses from the input
   */
  private parseExistingLicenses(): void {
    this.selectedLicenses.clear();

    if (this.driversLicenses?.driversLicenses) {
      try {
        const licenses: string[] = JSON.parse(this.driversLicenses.driversLicenses);
        licenses.forEach((license) => this.selectedLicenses.add(license));
      } catch {
        // If parsing fails, the string might already be a single license
        if (typeof this.driversLicenses.driversLicenses === 'string' && this.driversLicenses.driversLicenses !== '[]') {
          this.selectedLicenses.add(this.driversLicenses.driversLicenses);
        }
      }
    }
  }

  /**
   * Get selected licenses as an array for display
   */
  getSelectedLicensesArray(): string[] {
    return Array.from(this.selectedLicenses).sort((a, b) => {
      return DRIVERS_LICENSE_TYPES.indexOf(a as DriversLicenseType) - DRIVERS_LICENSE_TYPES.indexOf(b as DriversLicenseType);
    });
  }

  /**
   * Check if a license is selected (during edit mode)
   */
  isLicenseSelected(license: string): boolean {
    return this.isEditing ? this.tempSelectedLicenses.has(license) : this.selectedLicenses.has(license);
  }

  /**
   * Toggle license selection (during edit mode)
   */
  toggleLicense(license: string): void {
    if (!this.isEditing) return;

    if (this.tempSelectedLicenses.has(license)) {
      this.tempSelectedLicenses.delete(license);
    } else {
      this.tempSelectedLicenses.add(license);
    }
  }

  /**
   * Enter edit mode
   */
  startEditing(): void {
    this.isEditing = true;
    this.tempSelectedLicenses = new Set(this.selectedLicenses);
  }

  /**
   * Cancel editing and revert changes
   */
  cancelEditing(): void {
    this.isEditing = false;
    this.tempSelectedLicenses.clear();
  }

  /**
   * Save the changes
   */
  saveChanges(): void {
    const licensesArray = Array.from(this.tempSelectedLicenses).sort((a, b) => {
      return DRIVERS_LICENSE_TYPES.indexOf(a as DriversLicenseType) - DRIVERS_LICENSE_TYPES.indexOf(b as DriversLicenseType);
    });

    this.driversLicenseChange.emit({
      action: 'save',
      licenses: licensesArray,
    });

    this.selectedLicenses = new Set(this.tempSelectedLicenses);
    this.isEditing = false;
    this.tempSelectedLicenses.clear();
  }

  /**
   * Check if there are any licenses to display
   */
  hasLicenses(): boolean {
    return this.selectedLicenses.size > 0;
  }
}
